import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { GlowLayer } from './glow.ts';
import { clamp01 } from './halls.ts';

/**
 * The ink in the water (El pozo 1, docs/SAGAS.md): Musgoviejo's square at night, the tower defense the other way round.
 * In the middle, where the node would be, the village well: a round stone curb, its crank frame and rope, ink seeping
 * over the lip and staining the flags round it. Round the square's rim, the eight houses the nightmares go for
 * (`rules.well.houses`): timber and plaster under thatch, their windows lit; as one is struck it smokes, then burns, and
 * burned it is a charred frame on its footing, windows dark, embers in it. Up one of the roads, the mill, and its stone:
 * once it is out (`s.well.stone`) a cold ring of light under it and a dotted trail down its way to the well say «push
 * this»; it rolls as it moves. On the seal it tips flat over the well, ink bursts out of the cracks, then nothing.
 *
 * Draw calls: the square (well, mill, stains; one merged mesh), the houses whole and the houses burned (one mesh each;
 * a house is shown or hidden by collapsing its own vertex range), their windows (one mesh, a burned house's go dark),
 * the millstone, a ring under it, a glow layer. No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['well']>;

const STONE = ['#6f7178', '#62646b', '#7a7c82'];
const TIMBER = ['#3b2c22', '#33261d', '#45342a'];
const PLASTER = ['#9a9184', '#8c8478', '#a39a8c'];
const THATCH = ['#6b5a3a', '#5e4f33', '#75623f'];
const CHAR = ['#1a1715', '#221d1a', '#14110f'];
const ASH = '#3a3532';
const INK = '#07060c';
const STAIN = '#0d0b14';
const WOOD = '#4a372b';
const PLANK = '#5a4130';
const MILLSTONE = '#8a8780';
const MILLSTONE_DARK = '#6a6862';
const WINDOW = new THREE.Color('#ffc58a');
const WINDOW_DARK = new THREE.Color('#0a0807');
const FIRE = new THREE.Color('#ff8a3a');
const FIRE_CORE = new THREE.Color('#ffe2b8');
const EMBER = new THREE.Color('#ff4f1a');
const COOL = new THREE.Color('#cfe8ff');
const COOL_DEEP = new THREE.Color('#7fb8ff');
const INK_GLOW = new THREE.Color('#5a3a8a');
const LAMP = new THREE.Color('#ffd6a0');
/** The millstone's radius and half its thickness (world). */
const SR = 0.48;
const X = new THREE.Vector3(1, 0, 0);
const Y = new THREE.Vector3(0, 1, 0);
const Z = new THREE.Vector3(0, 0, 1);
const ST = 0.11;

/** A merged mesh whose pieces (one range each) can be shown or hidden by collapsing their vertices. */
class Ranges {
  readonly mesh: THREE.Mesh;
  private orig: Float32Array;
  private ranges: [number, number][] = [];
  private shown: boolean[] = [];

  constructor(parts: (THREE.BufferGeometry | null)[], mat: THREE.Material, name: string, visible: boolean) {
    let at = 0;
    const geos: THREE.BufferGeometry[] = [];
    for (const g of parts) {
      const n = g ? g.getAttribute('position').count : 0;
      this.ranges.push([at, n]);
      this.shown.push(true);
      at += n;
      if (g) geos.push(g);
    }
    const merged = geos.length ? (mergeGeometries(geos, false) ?? new THREE.BufferGeometry()) : new THREE.BufferGeometry();
    for (const g of geos) g.dispose();
    this.mesh = new THREE.Mesh(merged, mat);
    this.mesh.name = name;
    this.mesh.frustumCulled = false;
    const pos = merged.getAttribute('position') as THREE.BufferAttribute | undefined;
    this.orig = pos ? new Float32Array(pos.array as Float32Array) : new Float32Array(0);
    if (!visible) for (let i = 0; i < this.ranges.length; i++) this.show(i, false);
  }

  show(i: number, on: boolean): void {
    if (this.shown[i] === on) return;
    this.shown[i] = on;
    const pos = this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    const r = this.ranges[i];
    if (!pos || !r) return;
    const a = pos.array as Float32Array;
    for (let k = r[0] * 3; k < (r[0] + r[1]) * 3; k++) a[k] = on ? (this.orig[k] as number) : 0;
    pos.needsUpdate = true;
  }

  /** Sets every vertex colour of piece `i`. */
  tint(i: number, c: THREE.Color): void {
    const col = this.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const r = this.ranges[i];
    if (!col || !r) return;
    for (let k = r[0]; k < r[0] + r[1]; k++) col.setXYZ(k, c.r, c.g, c.b);
    col.needsUpdate = true;
  }
}

export class WellLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(220);
  private whole: Ranges;
  private burned: Ranges;
  private windows: Ranges;
  private stone: THREE.Mesh;
  private ring: THREE.Mesh;
  private ringMat: THREE.MeshBasicMaterial;
  private inkTop: THREE.Mesh;
  private inkMat: THREE.MeshBasicMaterial;
  /** Each house: where it stands (world), its windows' glow points, its chimney top, burned yet. */
  private houses: { p: THREE.Vector3; wins: THREE.Vector3[]; top: THREE.Vector3; burnt: boolean }[] = [];
  /** The well's mouth (its ink surface) and the mill's lamp. */
  private mouth = new THREE.Vector3();
  private lamp = new THREE.Vector3();
  /** The stone's way down its road to the well (world, on the ground). */
  private route: THREE.Vector3[] = [];
  /** The stone: where it was the tick before and now (world), its roll, which way it rolls, when it settled on the well. */
  private prev = new THREE.Vector3();
  private cur = new THREE.Vector3();
  private roll = 0;
  private yaw = 0;
  private sealedAt = 0;
  private seen = false;
  private now = 0;
  private v = new THREE.Vector3();
  private q = new THREE.Quaternion();
  private qa = new THREE.Quaternion();

  constructor(
    private ctx: OmenCtx,
    private r: Rules,
  ) {
    this.group.name = 'well-square';
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
    const ringG = new THREE.CylinderGeometry(0.5, 0.5, 1, 14, 1, true);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const disc = new THREE.CircleGeometry(0.5, 14).rotateX(-Math.PI / 2);
    // A gable prism along x, ridge at y = 1, base 1 deep.
    const gable = new THREE.CylinderGeometry(0.5, 0.5, 1, 3, 1).rotateZ(Math.PI / 2).rotateX(-Math.PI / 2);
    gable.computeBoundingBox();
    const bb = gable.boundingBox as THREE.Box3;
    gable.translate(0, -bb.min.y, 0).scale(1, 1 / (bb.max.y - bb.min.y), 1 / (bb.max.z - bb.min.z));

    const kit = new Kit();
    this.buildWell(kit, box, cyl, ringG, rock, disc);
    this.buildMill(kit, box, cyl, gable, rock);

    // The houses: each whole and each burned, built apart so either can be shown on its own.
    const wholeParts: (THREE.BufferGeometry | null)[] = [];
    const burnedParts: (THREE.BufferGeometry | null)[] = [];
    const winParts: (THREE.BufferGeometry | null)[] = [];
    const tmp = new THREE.MeshBasicMaterial();
    r.houses.forEach((h, i) => {
      const p = ctx.at(h.x, h.y, 0);
      const yaw = Math.atan2(-p.x, -p.z);
      const a = new Kit();
      const b = new Kit();
      const w = new Kit();
      const info = this.house(a, b, w, box, cyl, gable, rock, p, yaw, i);
      this.houses.push({ p, ...info, burnt: false });
      for (const [k, list] of [
        [a, wholeParts],
        [b, burnedParts],
        [w, winParts],
      ] as const)
        list.push(k.build(tmp, 'part')?.geometry ?? null);
    });
    tmp.dispose();

    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0, flatShading: true });
    const winMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(mat, winMat);
    const square = kit.build(mat, 'well-square');
    if (square) {
      square.receiveShadow = true;
      this.geos.push(square.geometry);
      this.group.add(square);
    }
    this.whole = new Ranges(wholeParts, mat, 'well-houses', true);
    this.burned = new Ranges(burnedParts, mat, 'well-houses-burned', false);
    this.windows = new Ranges(winParts, winMat, 'well-windows', true);
    for (const m of [this.whole.mesh, this.burned.mesh]) {
      m.receiveShadow = true;
      m.castShadow = !ctx.low;
    }
    for (const rg of [this.whole, this.burned, this.windows]) {
      this.geos.push(rg.mesh.geometry);
      this.group.add(rg.mesh);
    }

    // The ink in the well's mouth: a dark disc that breathes a bruise-purple, gone once the stone is on it.
    this.inkMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(INK) });
    this.mats.push(this.inkMat);
    const inkGeo = new THREE.CircleGeometry(0.33, 16).rotateX(-Math.PI / 2);
    this.geos.push(inkGeo);
    this.inkTop = new THREE.Mesh(inkGeo, this.inkMat);
    this.inkTop.position.copy(this.mouth);
    this.group.add(this.inkTop);

    // The millstone: a thick disc on edge, a square eye in the middle, dressing grooves on its faces.
    const sk = new Kit();
    sk.add(new THREE.CylinderGeometry(SR, SR, ST * 2, 18).rotateZ(Math.PI / 2), MILLSTONE, place(0, 0, 0));
    sk.add(new THREE.CylinderGeometry(SR * 1.01, SR * 1.01, ST * 0.5, 18).rotateZ(Math.PI / 2), MILLSTONE_DARK, place(0, 0, 0));
    for (const sx of [-1, 1]) {
      sk.add(box, '#1c1a18', place(sx * (ST + 0.002), 0, 0, 0, new THREE.Vector3(0.01, 0.12, 0.12)));
      for (let k = 0; k < 6; k++) {
        const a = (k / 6) * Math.PI * 2;
        sk.add(box, MILLSTONE_DARK, place(sx * (ST + 0.003), Math.sin(a) * SR * 0.55, Math.cos(a) * SR * 0.55, 0, new THREE.Vector3(0.008, 0.03, SR * 0.7), a, 0));
      }
    }
    const stoneMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0, flatShading: true });
    this.mats.push(stoneMat);
    this.stone = sk.build(stoneMat, 'millstone') as THREE.Mesh;
    this.geos.push(this.stone.geometry);
    this.stone.castShadow = !ctx.low;
    this.stone.frustumCulled = false;
    this.group.add(this.stone);

    // The cold ring of light under the stone while it waits to be pushed.
    this.ringMat = new THREE.MeshBasicMaterial({ color: COOL.clone(), transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.mats.push(this.ringMat);
    const rg = new THREE.RingGeometry(0.62, 0.74, 40).rotateX(-Math.PI / 2);
    this.geos.push(rg);
    this.ring = new THREE.Mesh(rg, this.ringMat);
    this.ring.renderOrder = 2;
    this.ring.frustumCulled = false;
    this.ring.visible = false;
    this.group.add(this.ring);

    // The stone's way, on the ground, and where it waits until it is out.
    this.route = r.route.map((p) => ctx.at(p.x, p.y, 0));
    this.cur.copy(this.route[0] ?? new THREE.Vector3());
    this.prev.copy(this.cur);
    const n = this.route[1];
    if (n) this.yaw = Math.atan2(n.x - this.cur.x, n.z - this.cur.z);

    this.group.add(this.glow.mesh);
    for (const g of [box, cyl, ringG, rock, disc, gable]) g.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** The village well on the node: a round curb, the crank frame, rope and bucket; ink stains on the flags round it. */
  private buildWell(kit: Kit, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry, ringG: THREE.CylinderGeometry, rock: THREE.DodecahedronGeometry, disc: THREE.BufferGeometry): void {
    const g = this.ctx.ground(0, 0);
    // The ink that got out over the square: thin dark runs from the curb outward, a few drying splashes.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.4;
      const d = 0.62 + (i % 2) * 0.12;
      kit.add(disc, STAIN, place(Math.cos(a) * d, g + 0.028, Math.sin(a) * d, -a, new THREE.Vector3(0.42 + (i % 3) * 0.1, 1, 0.12 + (i % 2) * 0.05)));
      kit.add(disc, STAIN, place(Math.cos(a + 0.2) * (d + 0.42), g + 0.027, Math.sin(a + 0.2) * (d + 0.42), a, 0.1 + (i % 3) * 0.04));
    }
    // The curb: two courses of stone, a capstone lip.
    kit.add(ringG, STONE[0] as string, place(0, g + 0.2, 0, 0, new THREE.Vector3(0.9, 0.4, 0.9)));
    kit.add(ringG, STONE[1] as string, place(0, g + 0.2, 0, 0, new THREE.Vector3(0.72, 0.4, 0.72)));
    kit.add(new THREE.TorusGeometry(0.41, 0.06, 5, 14).rotateX(Math.PI / 2), STONE[2] as string, place(0, g + 0.41, 0));
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      kit.add(box, i % 2 ? '#5a5c63' : '#6c6e75', place(Math.cos(a) * 0.455, g + 0.12 + (i % 2) * 0.16, Math.sin(a) * 0.455, -a, new THREE.Vector3(0.05, 0.13, 0.22)));
    }
    // Ink run over the lip and down its side.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.7;
      kit.add(box, INK, place(Math.cos(a) * 0.465, g + 0.26, Math.sin(a) * 0.465, -a, new THREE.Vector3(0.02, 0.28 - (i % 2) * 0.08, 0.06 + (i % 3) * 0.03)));
    }
    this.mouth.set(0, g + 0.36, 0);
    // The crank frame: two posts, a beam with a windlass, a little roof, the rope and its bucket at the lip.
    for (const sx of [-1, 1]) kit.add(box, WOOD, place(sx * 0.48, g + 0.6, 0, 0, new THREE.Vector3(0.07, 1.2, 0.07)));
    kit.add(cyl, PLANK, place(0, g + 0.92, 0, 0, new THREE.Vector3(0.1, 0.9, 0.1), 0, Math.PI / 2));
    kit.add(box, WOOD, place(0.56, g + 0.92, 0.08, 0, new THREE.Vector3(0.03, 0.03, 0.18)));
    kit.add(box, WOOD, place(0.56, g + 0.92, 0.17, 0, new THREE.Vector3(0.03, 0.12, 0.03)));
    kit.add(box, '#2a2622', place(0.05, g + 0.68, 0, 0, new THREE.Vector3(0.012, 0.44, 0.012)));
    kit.add(cyl, PLANK, place(0.05, g + 0.42, 0.0, 0, new THREE.Vector3(0.17, 0.16, 0.17)));
    kit.add(cyl, '#2f3137', place(0.05, g + 0.46, 0.0, 0, new THREE.Vector3(0.18, 0.025, 0.18)));
    kit.add(new THREE.CylinderGeometry(0.5, 0.5, 1, 3, 1).rotateZ(Math.PI / 2).rotateX(-Math.PI / 2), '#3b3f47', place(0, g + 1.2, 0, 0, new THREE.Vector3(1.05, 0.2, 0.42)));
    // Rocks and a dropped pail by the curb.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.3;
      kit.add(rock, STONE[i % STONE.length] as string, place(Math.cos(a) * 0.62, g + 0.04, Math.sin(a) * 0.62, i, 0.13));
    }
    kit.add(cyl, PLANK, place(-0.62, g + 0.08, 0.42, 0, new THREE.Vector3(0.18, 0.16, 0.18), Math.PI / 2, 0.3));
  }

  /** The mill up its road: a stone-footed timber house, a water wheel, a lamp at the door, beside where the stone waits. */
  private buildMill(kit: Kit, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry, gable: THREE.BufferGeometry, rock: THREE.DodecahedronGeometry): void {
    const ctx = this.ctx;
    const route = this.r.route.map((p) => ctx.at(p.x, p.y, 0));
    const a = route[0];
    const b = route[1] ?? new THREE.Vector3();
    if (!a) return;
    // Beside the road, on the side away from the middle, facing it.
    const dir = b.clone().sub(a).setY(0).normalize();
    const side = new THREE.Vector3(-dir.z, 0, dir.x);
    const out = a.clone().add(side.clone().multiplyScalar(1.55)).add(dir.clone().multiplyScalar(-0.55));
    out.y = ctx.ground(out.x, out.z);
    const yaw = Math.atan2(a.x - out.x, a.z - out.z);
    const m = place(out.x, out.y, out.z, yaw, 1.05);
    kit.add(box, STONE[1] as string, m, place(0, 0.1, 0, 0, new THREE.Vector3(1.0, 0.2, 0.85)));
    kit.add(box, PLASTER[1] as string, m, place(0, 0.48, 0, 0, new THREE.Vector3(0.92, 0.6, 0.78)));
    for (const x of [-0.46, 0.46]) for (const z of [-0.39, 0.39]) kit.add(box, TIMBER[0] as string, m, place(x, 0.48, z, 0, new THREE.Vector3(0.07, 0.6, 0.07)));
    kit.add(gable, THATCH[0] as string, m, place(0, 0.78, 0, Math.PI / 2, new THREE.Vector3(1.05, 0.5, 1.15)));
    kit.add(box, '#241a14', m, place(0.1, 0.36, 0.4, 0, new THREE.Vector3(0.2, 0.36, 0.03)));
    // The water wheel on its side, a trough over it.
    const wm = place(-0.58, 0.42, 0, 0);
    kit.add(new THREE.TorusGeometry(0.36, 0.035, 5, 16).rotateY(Math.PI / 2), PLANK, m, wm);
    for (let k = 0; k < 8; k++) {
      const t = (k / 8) * Math.PI * 2;
      kit.add(box, WOOD, m, wm.clone().multiply(place(0, Math.sin(t) * 0.25, Math.cos(t) * 0.25, 0, new THREE.Vector3(0.12, 0.04, 0.24), t, 0)));
    }
    kit.add(cyl, '#2f3137', m, wm.clone().multiply(place(0, 0, 0, 0, new THREE.Vector3(0.06, 0.22, 0.06), 0, Math.PI / 2)));
    kit.add(box, PLANK, m, place(-0.58, 0.86, 0, 0, new THREE.Vector3(0.14, 0.06, 0.9)));
    // Sacks of flour by the door, a lamp on a bracket.
    for (let k = 0; k < 3; k++) kit.add(rock, '#c9c0ae', m, place(0.38 + (k % 2) * 0.12, 0.08 + (k === 2 ? 0.1 : 0), 0.55 + (k > 0 ? 0.05 : 0), k, new THREE.Vector3(0.14, 0.11, 0.12)));
    kit.add(box, TIMBER[0] as string, m, place(-0.2, 0.62, 0.45, 0, new THREE.Vector3(0.03, 0.03, 0.14)));
    kit.add(box, '#ffd6a0', m, place(-0.2, 0.56, 0.52, 0, new THREE.Vector3(0.06, 0.08, 0.06)));
    this.lamp.set(-0.2, 0.56, 0.52).applyMatrix4(m);
  }

  /**
   * One house, whole (`a`), burned (`b`) and its windows (`w`), at `p` with its door to the well. Returns its windows'
   * glow points and its chimney's top.
   */
  private house(a: Kit, b: Kit, w: Kit, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry, gable: THREE.BufferGeometry, rock: THREE.DodecahedronGeometry, p: THREE.Vector3, yaw: number, i: number): { wins: THREE.Vector3[]; top: THREE.Vector3 } {
    const g = place(p.x, p.y, p.z, yaw, 1.1);
    const W = 0.98 + (i % 3) * 0.08;
    const D = 0.74 + (i % 2) * 0.08;
    const H = 0.52 + (i % 4) * 0.03;
    const RH = 0.52;
    const tim = TIMBER[i % TIMBER.length] as string;
    // Whole: stone footing, plaster framed in dark timber, a door to the well, thatch, a stone chimney.
    a.add(box, STONE[i % 3] as string, g, place(0, 0.08, 0, 0, new THREE.Vector3(W + 0.06, 0.16, D + 0.06)));
    a.add(box, PLASTER[i % 3] as string, g, place(0, 0.16 + H / 2, 0, 0, new THREE.Vector3(W, H, D)));
    for (const x of [-W / 2, W / 2]) for (const z of [-D / 2, D / 2]) a.add(box, tim, g, place(x, 0.16 + H / 2, z, 0, new THREE.Vector3(0.07, H, 0.07)));
    a.add(box, tim, g, place(0, 0.16 + H, D / 2 + 0.005, 0, new THREE.Vector3(W, 0.06, 0.04)));
    a.add(box, tim, g, place(-W / 4, 0.16 + H / 2, D / 2 + 0.01, 0.7, new THREE.Vector3(0.04, H * 1.1, 0.03), 0, 0.7));
    a.add(box, '#241a14', g, place(W * 0.2, 0.16 + 0.19, D / 2 + 0.012, 0, new THREE.Vector3(0.2, 0.38, 0.03)));
    a.add(box, STONE[(i + 1) % 3] as string, g, place(W * 0.2, 0.05, D / 2 + 0.1, 0, new THREE.Vector3(0.3, 0.06, 0.16)));
    a.add(gable, THATCH[i % 3] as string, g, place(0, 0.16 + H, 0, 0, new THREE.Vector3(W + 0.24, RH, D + 0.32)));
    a.add(box, '#4a3d28', g, place(0, 0.16 + H + RH - 0.01, 0, 0, new THREE.Vector3(W + 0.28, 0.06, 0.1)));
    const cx = (i % 2 ? -1 : 1) * W * 0.3;
    a.add(box, STONE[i % 3] as string, g, place(cx, 0.16 + H + RH * 0.75, -D * 0.12, 0, new THREE.Vector3(0.13, RH * 0.9, 0.13)));
    const top = new THREE.Vector3(cx, 0.16 + H + RH * 1.25, -D * 0.12).applyMatrix4(g);
    // A barrel and a bench by the door.
    a.add(cyl, PLANK, g, place(-W * 0.38, 0.14, D / 2 + 0.18, 0, new THREE.Vector3(0.18, 0.28, 0.18)));
    a.add(box, WOOD, g, place(W * 0.42, 0.1, D / 2 + 0.16, 0, new THREE.Vector3(0.3, 0.04, 0.1)));
    // The windows: a dark frame on the house, the lit pane in its own mesh.
    const wins: THREE.Vector3[] = [];
    const spots: [number, number, number][] = [
      [-W * 0.22, D / 2 + 0.014, 0],
      [W / 2 + 0.014, 0, Math.PI / 2],
      [-W / 2 - 0.014, 0, -Math.PI / 2],
    ];
    for (const [wx, wz, ry] of spots) {
      const m = place(wx, 0.16 + H * 0.56, wz, ry, 1);
      a.add(box, tim, g, m.clone().multiply(new THREE.Matrix4().makeScale(0.21, 0.21, 0.02)));
      w.add(box, WINDOW, g, m.clone().multiply(new THREE.Matrix4().compose(new THREE.Vector3(0, 0, 0.006), new THREE.Quaternion(), new THREE.Vector3(0.16, 0.16, 0.02))));
      wins.push(new THREE.Vector3(0, 0, 0.05).applyMatrix4(m).applyMatrix4(g));
    }
    // Burned: the footing blackened, the four corner posts charred and broken, a fallen beam, ash and rubble.
    b.add(box, CHAR[0] as string, g, place(0, 0.08, 0, 0, new THREE.Vector3(W + 0.06, 0.16, D + 0.06)));
    b.add(box, ASH, g, place(0, 0.17, 0, 0, new THREE.Vector3(W - 0.05, 0.03, D - 0.05)));
    const posts: [number, number, number][] = [
      [-W / 2, -D / 2, 0.7],
      [W / 2, -D / 2, 0.45],
      [-W / 2, D / 2, 0.35],
      [W / 2, D / 2, 0.6],
    ];
    posts.forEach(([x, z, h], k) => b.add(box, CHAR[k % 3] as string, g, place(x, 0.16 + h / 2, z, 0, new THREE.Vector3(0.08, h, 0.08), (k - 1.5) * 0.08, 0)));
    b.add(box, CHAR[1] as string, g, place(0, 0.4, -D / 2, 0, new THREE.Vector3(W, 0.06, 0.06), 0, 0.35));
    b.add(box, CHAR[2] as string, g, place(-0.1, 0.24, 0.05, 0.6, new THREE.Vector3(W * 0.9, 0.06, 0.06), 0, 0.15));
    b.add(box, STONE[i % 3] as string, g, place(cx, 0.16 + 0.32, -D * 0.12, 0, new THREE.Vector3(0.13, 0.64, 0.13)));
    for (let k = 0; k < 7; k++) b.add(rock, k % 2 ? CHAR[0] as string : ASH, g, place(-W * 0.35 + (k % 4) * W * 0.23, 0.2 + (k % 3) * 0.03, -D * 0.25 + Math.floor(k / 4) * D * 0.45, k, 0.12 + (k % 3) * 0.04));
    return { wins, top };
  }

  /** Every fresh tick: ink welling in the mouth, smoke and fire off struck houses, the events' sparks and flares. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    const st = s.well;
    // The stone: last tick's place and this one's (for drawing between).
    this.prev.copy(this.cur);
    if (st?.stone) {
      const p = this.ctx.at(st.stone.x, st.stone.y, 0);
      const dx = p.x - this.cur.x;
      const dz = p.z - this.cur.z;
      const d = Math.hypot(dx, dz);
      if (d > 1e-4) {
        this.roll += d / SR;
        this.yaw = Math.atan2(dx, dz);
      }
      this.cur.copy(p);
      if (!this.seen) this.prev.copy(p);
      this.seen = true;
    }
    if (st?.sealed && !this.sealedAt) this.sealedAt = this.now || 1e-6;
    // Ink welling over the lip while the well is open.
    if (!st?.sealed && s.tick % (low ? 6 : 3) === 0) {
      const a = (s.tick * 1.7) % (Math.PI * 2);
      this.v.set(Math.cos(a) * 0.4, this.mouth.y, Math.sin(a) * 0.4);
      this.ctx.vfx.burst(this.v, { n: 1, color: s.tick % 2 ? '#1a1228' : '#0a0810', speed: 0.004, up: 0.005, size: 0.07, life: 22, gravity: 0.004, spread: 0.05 });
    }
    // The houses: smoke once struck hard, fire when nearly gone, embers and smoke when burned.
    const hp = st?.hp ?? [];
    this.houses.forEach((h, i) => {
      const left = (hp[i] ?? this.r.hp) / Math.max(1, this.r.hp);
      if (left <= 0 && !h.burnt) {
        h.burnt = true;
        this.whole.show(i, false);
        this.burned.show(i, true);
        this.windows.tint(i, WINDOW_DARK);
      }
      const every = h.burnt ? (low ? 12 : 6) : left < 0.4 ? (low ? 4 : 2) : left < 0.75 ? (low ? 12 : 6) : 0;
      if (!every || (s.tick + i * 3) % every !== 0) return;
      const at = h.burnt ? this.v.copy(h.p).setY(h.p.y + 0.35) : h.top;
      this.ctx.vfx.burst(at, { n: 1, color: left < 0.4 && !h.burnt ? '#2a2420' : '#3a3438', speed: 0.004, up: 0.022, size: 0.5, life: 44, smoke: true, spread: 0.18 });
      if (left < 0.4 || h.burnt) this.ctx.vfx.burst(this.v.copy(h.p).setY(h.p.y + (h.burnt ? 0.3 : 0.6)), { n: 1, color: s.tick % 2 ? '#ff8a3a' : '#ff4f1a', speed: 0.01, up: 0.04, size: 0.05, life: 18, gravity: -0.002, spread: 0.4 });
    });
    for (const e of s._events ?? []) {
      if (e.t !== 'well') continue;
      const p = this.ctx.at(e.x, e.y, 0.4);
      if (e.kind === 'hit') {
        if (s.tick % 2 === 0) this.ctx.vfx.burst(p, { n: low ? 2 : 4, color: '#ffb070', speed: 0.04, up: 0.03, size: 0.04, life: 10, gravity: 0.006, spread: 0.3 });
      } else if (e.kind === 'burn') {
        this.ctx.flare(p.clone().setY(p.y + 0.6), '#ff8a3a', 2.6, 14);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 8 : 20, color: '#ff8a3a', speed: 0.06, up: 0.08, size: 0.07, life: 22, gravity: -0.002, spread: 0.6 });
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 4 : 9, color: '#26232a', speed: 0.02, up: 0.05, size: 0.6, life: 40, smoke: true, spread: 0.5 });
        this.ctx.shake(0.06);
      } else if (e.kind === 'stone') {
        // Out of the mill: a cold flash where it waits.
        const sp = this.route[0] ?? p;
        this.ctx.flare(sp.clone().setY(sp.y + 0.6), '#cfe8ff', 2.2, 14);
        this.ctx.vfx.ring(sp.clone().setY(sp.y + 0.06), 0.3, 1.6, '#cfe8ff', 14, 0.8);
      } else if (e.kind === 'seal') {
        // On the well: a dull thud, ink out of every crack, then quiet.
        this.ctx.shake(0.14);
        this.ctx.vfx.ring(this.mouth.clone().setY(this.mouth.y - 0.25), 0.4, 2.4, '#3a2a5a', 16, 0.8);
        this.ctx.vfx.burst(this.mouth.clone().setY(this.mouth.y + 0.15), { n: low ? 10 : 26, color: '#0c0914', speed: 0.07, up: 0.05, size: 0.09, life: 26, gravity: 0.008, spread: 0.6 });
        this.ctx.vfx.burst(this.mouth.clone().setY(this.mouth.y + 0.2), { n: low ? 3 : 7, color: '#2a2438', speed: 0.02, up: 0.03, size: 0.5, life: 34, smoke: true, spread: 0.5 });
      }
    }
  }

  /** Every redraw: the millstone (rolling, or tipping flat onto the well), its ring and trail, the ink, the glows. */
  draw(s: BattleState, alpha: number): void {
    const st = s.well;
    const out = !!st?.stone;
    const sealed = !!st?.sealed;
    // The stone: between last tick's place and this one's; tipping flat over the well once sealed.
    const p = this.v.copy(this.prev).lerp(this.cur, clamp01(alpha));
    let tip = 0;
    if (sealed) {
      p.set(0, this.mouth.y + 0.06, 0);
      tip = clamp01((this.now - this.sealedAt) / 0.45);
      const ease = 1 - (1 - tip) * (1 - tip);
      p.y = this.mouth.y + 0.06 + (1 - ease) * (SR - 0.06);
    } else p.y = this.ctx.ground(p.x, p.z) + SR;
    this.stone.position.copy(p);
    // The disc is modelled with its axle along x. Facing its way (yaw), the axle is across it: rolling turns it about
    // the axle; settling on the well tips it over sideways (about its way) until it lies flat.
    this.q.setFromAxisAngle(Y, this.yaw);
    if (sealed) this.q.multiply(this.qa.setFromAxisAngle(Z, (Math.PI / 2) * (1 - (1 - tip) * (1 - tip))));
    else this.q.multiply(this.qa.setFromAxisAngle(X, this.roll));
    this.stone.quaternion.copy(this.q);
    this.inkTop.visible = !sealed || tip < 0.9;
    const breathe = 0.5 + 0.5 * Math.sin(this.now * 1.6);
    this.inkMat.color.set(INK).lerp(INK_GLOW, 0.25 * breathe);

    // The ring under it, pulsing, while it is out and not on the well.
    const pulse = 0.5 + 0.5 * Math.sin(this.now * 4);
    this.ring.visible = out && !sealed;
    if (this.ring.visible) {
      this.ring.position.set(p.x, this.ctx.ground(p.x, p.z) + 0.05, p.z);
      this.ring.scale.setScalar(0.95 + 0.12 * pulse);
      this.ringMat.opacity = 0.35 + 0.35 * pulse;
    }

    this.glow.begin();
    // The windows of the houses still standing; a burning house's glow, a burned one's embers.
    const hp = st?.hp ?? [];
    this.houses.forEach((h, i) => {
      const left = (hp[i] ?? this.r.hp) / Math.max(1, this.r.hp);
      if (h.burnt) {
        const f = 0.7 + 0.3 * Math.sin(this.now * 3 + i);
        this.glow.add(h.p.x, h.p.y + 0.25, h.p.z, EMBER, 1.0, 0.3 * f);
        return;
      }
      const flick = left < 0.4 ? 0.6 + 0.4 * Math.sin(this.now * 11 + i * 2) : 0.9 + 0.1 * Math.sin(this.now * 3 + i);
      for (const wp of h.wins) {
        this.glow.add(wp.x, wp.y, wp.z, WINDOW, 0.5, 0.5 * flick);
        this.glow.add(wp.x, this.ctx.ground(wp.x, wp.z) + 0.04, wp.z, WINDOW, 1.0, 0.1 * flick);
      }
      if (left < 0.4) {
        const f = 0.7 + 0.3 * Math.sin(this.now * 9 + i);
        this.glow.add(h.p.x, h.p.y + 0.9, h.p.z, FIRE_CORE, 0.6, 0.6 * f);
        this.glow.add(h.p.x, h.p.y + 0.7, h.p.z, FIRE, 1.8, 0.45 * f);
      }
    });
    // The mill's lamp.
    this.glow.add(this.lamp.x, this.lamp.y, this.lamp.z, LAMP, 0.55, 0.75);
    this.glow.add(this.lamp.x, this.ctx.ground(this.lamp.x, this.lamp.z) + 0.04, this.lamp.z, LAMP, 1.6, 0.14);
    // The well's ink, a bruise-purple breathing in the mouth while it is open.
    if (!sealed) this.glow.add(this.mouth.x, this.mouth.y + 0.05, this.mouth.z, INK_GLOW, 1.0, 0.25 + 0.15 * breathe);
    // The stone out: a cold light on it, and a dotted trail of light down its way to the well, flowing toward it.
    if (out && !sealed && st?.stone) {
      this.glow.add(p.x, p.y + 0.1, p.z, COOL, 1.2, 0.3 + 0.2 * pulse);
      const from = st.stone.at + 1;
      const pts = [p.clone(), ...this.route.slice(from)];
      const step = 0.55;
      let carry = (this.now * 0.9) % step;
      for (let k = 0; k < pts.length - 1; k++) {
        const a = pts[k] as THREE.Vector3;
        const b = pts[k + 1] as THREE.Vector3;
        const len = Math.hypot(b.x - a.x, b.z - a.z);
        for (let d = carry; d < len; d += step) {
          const t = d / len;
          const x = a.x + (b.x - a.x) * t;
          const z = a.z + (b.z - a.z) * t;
          this.glow.add(x, this.ctx.ground(x, z) + 0.06, z, COOL_DEEP, 0.32, 0.5);
        }
        carry = (carry - len) % step;
        if (carry < 0) carry += step;
      }
    }
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
